import { SagaIterator } from '@redux-saga/types';
import { Task } from 'redux-saga';
import { call, cancel, delay, fork, put } from 'redux-saga/effects';
import { AuthenticationSucceed } from '@signageos/actions/dist/Authentication/authenticationActions';
import Debug from 'debug';
import { MAX_PERIODIC_TELEMETRY_INVALIDATION_INTERVAL, MIN_PERIODIC_TELEMETRY_INVALIDATION_INTERVAL } from '../../../Intervals/constants';
import Property from '../../../Property/Property';
import { increaseConstantByRandomValueFromInterval } from './deviceTelemetryHelper';
import { IPropertyStorage } from '../../../Property/propertyStorage';
import { bindAndTakeEvery } from '../../../Socket/socketActionCreator';

const debug = Debug('@signageos/front-display:Device:Telemetry:periodicTelemetryInvalidation');

const VARIABLE_PERIOD = MAX_PERIODIC_TELEMETRY_INVALIDATION_INTERVAL - MIN_PERIODIC_TELEMETRY_INVALIDATION_INTERVAL;

export function* periodicTelemetryInvalidationSaga(propertyStorage: IPropertyStorage): SagaIterator {
	let scheduledTask: Task | null = null;

	yield bindAndTakeEvery(AuthenticationSucceed, function* (): any {
		try {
			const stored = yield call([propertyStorage, propertyStorage.getValueOrDefault], Property.TELEMETRY_NEXT_INVALIDATION_AT, null);
			const now = Date.now();

			let target: number;
			if (typeof stored === 'number' && now < stored) {
				target = stored;
				debug(`Telemetry will be invalidated at ${new Date(target).toISOString()} (persisted)`);
			} else {
				if (typeof stored === 'number') {
					debug('Persisted invalidation time elapsed during downtime; invalidating telemetry now');
					yield put({ type: Property.TELEMETRY_INVALIDATION });
				}
				target = yield call(scheduleNextTarget, propertyStorage);
			}

			// Re-arm the in-process timer for the remaining time so a device that stays up and
			// connected (never reboots/reconnects) still invalidates once the target is reached,
			// instead of only checking on the next authentication. Cancelling the previous task
			// prevents frequent reconnects from piling up concurrent timers.
			if (scheduledTask) {
				yield cancel(scheduledTask);
			}
			scheduledTask = yield fork(invalidateWhenDue, propertyStorage, target);
		} catch (e) {
			debug('Failed to perform telemetry invalidation check', e);
		}
	});
}

function* invalidateWhenDue(propertyStorage: IPropertyStorage, target: number): SagaIterator {
	let nextTarget = target;
	while (true) {
		try {
			yield delay(Math.max(0, nextTarget - Date.now()));
			debug('Scheduled telemetry invalidation timer elapsed; invalidating telemetry now');
			yield put({ type: Property.TELEMETRY_INVALIDATION });
			nextTarget = yield call(scheduleNextTarget, propertyStorage);
		} catch (e) {
			debug('Failed to invalidate telemetry on scheduled timer', e);
			return;
		}
	}
}

function* scheduleNextTarget(propertyStorage: IPropertyStorage): SagaIterator {
	const delayInterval = increaseConstantByRandomValueFromInterval(MIN_PERIODIC_TELEMETRY_INVALIDATION_INTERVAL, VARIABLE_PERIOD);
	const target = Date.now() + delayInterval;
	yield call([propertyStorage, propertyStorage.setValue], Property.TELEMETRY_NEXT_INVALIDATION_AT, target);
	debug(`Saved next telemetry invalidation target: ${new Date(target).toISOString()}`);
	return target;
}
